import { useState, useEffect, useCallback, useRef } from 'react';
import { createExport, getExportStatus, processExport } from '@/api/admin-exports';
import type { Export, ExportStatus } from '@/types/admin-export';

interface UseExportPollingOptions {
  pollingInterval?: number; // ms, default 1000
  onComplete?: (exportData: Export) => void;
  onError?: (error: Error) => void;
}

interface UseExportPollingReturn {
  exportData: Export | null;
  status: ExportStatus | null;
  progress: number;
  isLoading: boolean;
  error: Error | null;
  startExport: (type: string, filters?: Record<string, unknown>) => Promise<void>;
  reset: () => void;
}

export function useExportPolling(options: UseExportPollingOptions = {}): UseExportPollingReturn {
  const {
    pollingInterval = 1000,
    onComplete,
    onError,
  } = options;

  const [exportData, setExportData] = useState<Export | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const exportIdRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    if (!exportIdRef.current) return;

    try {
      const data = await getExportStatus(exportIdRef.current);
      setExportData(data);

      if (data.status === 'completed') {
        stopPolling();
        setIsLoading(false);
        onComplete?.(data);
      } else if (data.status === 'failed') {
        stopPolling();
        setIsLoading(false);
        const err = new Error(data.errorMessage || 'Export failed');
        setError(err);
        onError?.(err);
      }
    } catch (err) {
      stopPolling();
      setIsLoading(false);
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      onError?.(error);
    }
  }, [stopPolling, onComplete, onError]);

  const startExport = useCallback(async (type: string, filters?: Record<string, unknown>) => {
    // Reset state
    setExportData(null);
    setError(null);
    setIsLoading(true);
    stopPolling();

    try {
      // 1. Create export
      const createResponse = await createExport({ type, filters });
      exportIdRef.current = createResponse.id;

      // 2. Start processing (this triggers the generation)
      // We don't await this - we'll poll for status
      processExport(createResponse.id).catch(() => {
        // Error will be caught by polling
      });

      // 3. Start polling
      pollingRef.current = setInterval(pollStatus, pollingInterval);

      // Initial poll
      await pollStatus();

    } catch (err) {
      setIsLoading(false);
      const error = err instanceof Error ? err : new Error('Failed to start export');
      setError(error);
      onError?.(error);
    }
  }, [stopPolling, pollStatus, pollingInterval, onError]);

  const reset = useCallback(() => {
    stopPolling();
    exportIdRef.current = null;
    setExportData(null);
    setError(null);
    setIsLoading(false);
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    exportData,
    status: exportData?.status ?? null,
    progress: exportData?.progress ?? 0,
    isLoading,
    error,
    startExport,
    reset,
  };
}
